import { SearchAggregations } from '@/__generated__/graphql';
import {
  CUSTOM_MATERIALIZATION_NO_MATCH_SENTINEL,
  CUSTOM_MATERIALIZATION_TYPE,
  materializationTypes,
} from '@/typings/dbt';

const KNOWN_TYPES: ReadonlySet<string> = new Set(materializationTypes);

export function getCustomMaterializationCount(
  aggregations: Partial<SearchAggregations> | null,
): number {
  const matTypes = aggregations?.dbtMaterializationTypes;
  if (!matTypes) return 0;

  return Object.entries(matTypes as Record<string, number>).reduce(
    (sum, [key, count]) => (KNOWN_TYPES.has(key) ? sum : sum + count),
    0,
  );
}

export function getCustomMaterializationTypeNames(
  aggregations: Partial<SearchAggregations> | null,
): string[] {
  const matTypes = aggregations?.dbtMaterializationTypes;
  if (!matTypes) return [];

  return Object.keys(matTypes as Record<string, number>).filter(
    (key) => !KNOWN_TYPES.has(key),
  );
}

/**
 * Builds a pseudo-aggregation record containing only the custom (non-standard)
 * materialization type names. Used to seed the SearchFacets context when the
 * searchv2 aggregation data isn't available (e.g. V1 path).
 *
 * Counts are set to 0 because the account.materializationTypes endpoint only
 * provides type names, not result counts.
 */
export function buildCustomMaterializationAggregation(
  typeNames: string[],
): Record<string, number> | null {
  const customNames = typeNames.filter((name) => !KNOWN_TYPES.has(name));
  if (customNames.length === 0) return null;
  return Object.fromEntries(customNames.map((name) => [name, 0]));
}

/**
 * Translates the UI-only `'custom'` filter token into concrete materialization
 * type names that Elasticsearch understands.
 *
 * The search API's `materializationTypes` parameter is a positive inclusion
 * filter -- it only accepts real type names (e.g. "microbatch",
 * "federated_view"), not negation/exclusion. So we can't say "everything
 * except the 5 known types"; we must enumerate the actual custom type names.
 * The only source of those names is the `dbtMaterializationTypes` aggregation
 * returned by a previous searchv2 call.
 *
 * When expansion produces an empty list (e.g. "Custom" selected but no custom
 * types exist in the aggregation data), we return a sentinel value
 * (`CUSTOM_MATERIALIZATION_NO_MATCH_SENTINEL`) instead of `[]`. The Codex API
 * treats `materializationTypes: []` identically to `null` (i.e. no filter),
 * which would show *all* results — the opposite of what the user intended.
 * The sentinel is an unmatchable string that Elasticsearch won't find in any
 * document, so it correctly yields zero results.
 */
export function expandCustomMaterializationTypes(
  filterTypes: string[],
  aggregations: Partial<SearchAggregations> | null,
): string[] {
  if (!filterTypes.includes(CUSTOM_MATERIALIZATION_TYPE)) {
    return [...filterTypes];
  }

  const withoutCustom = filterTypes.filter((t) => t !== CUSTOM_MATERIALIZATION_TYPE);
  const customTypeNames = getCustomMaterializationTypeNames(aggregations);
  const expanded = [...withoutCustom, ...customTypeNames];

  if (expanded.length === 0) {
    return [CUSTOM_MATERIALIZATION_NO_MATCH_SENTINEL];
  }
  return expanded;
}
